import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../App';
import {
  House,
  Compass,
  ChartBar,
  SignOut,
  ForkKnife,
  User,
  Database,
  Gift,
  GearSix,
  MapTrifold,
  Sparkle,
  Trophy,
  MapPin,
  List,
  Clock,
  Heart,
  Folder,
} from '@phosphor-icons/react';
import { Button } from './ui/button';
import {
  Sheet,
  SheetContent,
  SheetTrigger,
  SheetClose,
} from './ui/sheet';

const NAV_ITEMS = [
  { path: '/', label: 'Dashboard', icon: House, auth: false },
  { path: '/discovery', label: 'Discovery', icon: Compass, auth: false },
  { path: '/timeline', label: 'Timeline', icon: Clock, auth: true },
  { path: '/concierge', label: 'AI Concierge', icon: Sparkle, auth: true },
  { path: '/collections', label: 'Collections', icon: Folder, auth: true },
  { path: '/dishes', label: 'Favourite Dishes', icon: Heart, auth: true },
  { path: '/stats', label: 'Stats', icon: ChartBar, auth: true },
  { path: '/wrapped', label: 'Wrapped', icon: Gift, auth: true },
  { path: '/map', label: 'Taste Map', icon: MapTrifold, auth: true },
  { path: '/challenges', label: 'Challenges', icon: Trophy, auth: true },
];

const Header = () => {
  const { user, isAuthenticated, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);

  const isActive = (path) => location.pathname === path;

  const handleLogout = () => {
    logout();
    navigate('/login');
    setOpen(false);
  };

  const handleNav = (path) => {
    navigate(path);
    setOpen(false);
  };

  return (
    <header className="header-blur" data-testid="main-header">
      <div className="container-app">
        <div className="flex items-center justify-between h-16">
          {/* Left: Menu trigger + Logo */}
          <div className="flex items-center gap-2">
            {/* Hamburger — always visible on desktop too */}
            <Sheet open={open} onOpenChange={setOpen}>
              <SheetTrigger asChild>
                <button
                  className="w-10 h-10 rounded-xl flex items-center justify-center text-[#7A7873] hover:text-[#1A1918] hover:bg-[#EAE8E3] transition-colors"
                  data-testid="menu-trigger"
                >
                  <List weight="bold" className="w-6 h-6" />
                </button>
              </SheetTrigger>

              <SheetContent side="left" className="w-72 p-0 bg-[#F9F8F5] border-r border-[#EAE8E3]">
                {/* Sidebar header */}
                <div className="flex items-center gap-3 px-6 h-16 border-b border-[#EAE8E3]">
                  <ForkKnife weight="duotone" className="w-7 h-7 text-[#A94438]" />
                  <span className="font-heading text-lg font-semibold text-[#1A1918]">
                    Dining Tracker
                  </span>
                </div>

                {/* User info */}
                {isAuthenticated && (
                  <div className="px-6 py-4 border-b border-[#EAE8E3]">
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 rounded-full bg-[#E8DCD0] flex items-center justify-center flex-shrink-0">
                        <User weight="duotone" className="w-5 h-5 text-[#A94438]" />
                      </div>
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-[#1A1918] truncate">
                          {user?.name || 'User'}
                        </p>
                        <p className="text-xs text-[#7A7873] truncate">
                          {user?.email}
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Nav links */}
                <nav className="px-3 py-3 flex-1 overflow-y-auto">
                  {NAV_ITEMS.filter((item) => !item.auth || isAuthenticated).map((item) => {
                    const Icon = item.icon;
                    const active = isActive(item.path);
                    return (
                      <button
                        key={item.path}
                        onClick={() => handleNav(item.path)}
                        className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl mb-0.5 transition-colors text-left ${
                          active
                            ? 'bg-[#A94438] text-white'
                            : 'text-[#4A4845] hover:bg-[#EAE8E3]/70'
                        }`}
                        data-testid={`nav-${item.path.replace('/', '') || 'dashboard'}`}
                      >
                        <Icon
                          weight={active ? 'fill' : 'duotone'}
                          className="w-5 h-5 flex-shrink-0"
                        />
                        <span className="text-sm font-medium">{item.label}</span>
                      </button>
                    );
                  })}

                  {/* Admin — only for admins */}
                  {isAuthenticated && user?.is_admin && (
                    <button
                      onClick={() => handleNav('/admin')}
                      className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl mb-0.5 transition-colors text-left ${
                        isActive('/admin')
                          ? 'bg-[#A94438] text-white'
                          : 'text-[#4A4845] hover:bg-[#EAE8E3]/70'
                      }`}
                      data-testid="nav-admin"
                    >
                      <Database
                        weight={isActive('/admin') ? 'fill' : 'duotone'}
                        className="w-5 h-5 flex-shrink-0"
                      />
                      <span className="text-sm font-medium">Admin</span>
                    </button>
                  )}
                </nav>

                {/* Bottom actions */}
                <div className="px-3 py-4 border-t border-[#EAE8E3] mt-auto">
                  {isAuthenticated && (
                    <>
                      <button
                        onClick={() => handleNav('/settings')}
                        className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl mb-0.5 transition-colors text-left ${
                          isActive('/settings')
                            ? 'bg-[#A94438] text-white'
                            : 'text-[#4A4845] hover:bg-[#EAE8E3]/70'
                        }`}
                        data-testid="nav-settings"
                      >
                        <GearSix
                          weight={isActive('/settings') ? 'fill' : 'duotone'}
                          className="w-5 h-5 flex-shrink-0"
                        />
                        <span className="text-sm font-medium">Settings</span>
                      </button>
                      <button
                        onClick={handleLogout}
                        className="w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-colors text-left text-[#A94438] hover:bg-red-50"
                        data-testid="logout-button"
                      >
                        <SignOut weight="duotone" className="w-5 h-5 flex-shrink-0" />
                        <span className="text-sm font-medium">Sign out</span>
                      </button>
                    </>
                  )}
                </div>
              </SheetContent>
            </Sheet>

            {/* Logo */}
            <Link to="/" className="flex items-center gap-2" data-testid="logo-link">
              <ForkKnife weight="duotone" className="w-7 h-7 text-[#A94438]" />
              <span className="font-heading text-lg font-semibold text-[#1A1918] hidden sm:inline">
                Dining Tracker
              </span>
            </Link>
          </div>

          {/* Right: Check In CTA + Auth */}
          <div className="flex items-center gap-3">
            {isAuthenticated && (
              <Link
                to="/checkin"
                className={`flex items-center gap-2 px-5 py-2.5 rounded-full font-semibold text-sm transition-all ${
                  isActive('/checkin')
                    ? 'bg-[#A94438] text-white shadow-md'
                    : 'bg-[#A94438]/10 text-[#A94438] hover:bg-[#A94438] hover:text-white hover:shadow-md'
                }`}
                data-testid="nav-checkin"
              >
                <MapPin weight="fill" className="w-5 h-5" />
                <span>Check In</span>
              </Link>
            )}

            {isAuthenticated ? (
              <button
                onClick={() => setOpen(true)}
                className="w-9 h-9 rounded-full bg-[#E8DCD0] flex items-center justify-center"
                data-testid="user-avatar-btn"
              >
                <User weight="duotone" className="w-5 h-5 text-[#A94438]" />
              </button>
            ) : (
              <Button
                onClick={() => navigate('/login')}
                className="btn-primary"
                data-testid="login-button"
              >
                Sign in
              </Button>
            )}
          </div>
        </div>
      </div>

    </header>
  );
};

export default Header;
